import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { SignedEditionSadaicWorkflowStateViewStore } from "../../store";
import { SignedEditionSadaicWorkflowStateView } from "../view/SignedEditionSadaicWorkflowStateView";
import { SignedEditionSadaicWorkflowStateUpdateStore } from "../../store";
import { SignedEditionSadaicWorkflowStateUpdate } from "../update/SignedEditionSadaicWorkflowStateUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type SignedEditionSadaicWorkflowStateModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const SignedEditionSadaicWorkflowStateModalContent: React.FC<SignedEditionSadaicWorkflowStateModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new SignedEditionSadaicWorkflowStateUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new SignedEditionSadaicWorkflowStateViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <SignedEditionSadaicWorkflowStateView
            signedEditionSadaicWorkflowStateViewStore={
              storeInfo.store as SignedEditionSadaicWorkflowStateViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <SignedEditionSadaicWorkflowStateUpdate
            signedEditionSadaicWorkflowStateUpdateStore={
              storeInfo.store as SignedEditionSadaicWorkflowStateUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
